import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE, getToken } from "../../lib/auth";

type InquiryRow = {
  id: number;
  user_id?: number | null;
  name: string;
  email: string;
  category: string;
  status: "open" | "answered" | "closed" | string;
  created_at: string;
};

const CAT_KO: Record<string, string> = {
  general: "서비스 문의",
  payment: "결제 관련",
  contract: "계약/서류",
  account: "계정/인증",
  tech: "오류 신고",
  etc: "기타",
};

const STATUS_KO: Record<string, string> = {
  open: "대기",
  answered: "답변완료",
  closed: "종료",
};

function formatCategory(v?: string | null) {
  if (!v) return "-";
  const key = String(v).toLowerCase();
  return CAT_KO[key] || v;
}

function formatStatus(v?: string | null) {
  if (!v) return "-";
  const key = String(v).toLowerCase();
  return STATUS_KO[key] || v;
}

function formatDate(s?: string | null) {
  if (!s) return "-";
  try {
    return new Date(s).toLocaleString();
  } catch {
    return s;
  }
}

export default function AdminInquiries() {
  const nav = useNavigate();

  const [rows, setRows] = useState<InquiryRow[]>([]);
  const [loading, setLoading] = useState(false);

  const [tab, setTab] = useState<"all" | "open" | "answered" | "closed">("all");
  const [q, setQ] = useState("");

  const filteredCount = useMemo(() => rows.length, [rows]);

  async function load() {
    setLoading(true);
    try {
      const statusParam = tab === "all" ? "" : tab;

      const url =
        `${API_BASE}/api/inquiries/admin` +
        `?status=${encodeURIComponent(statusParam)}` +
        `&q=${encodeURIComponent(q.trim())}`;

      const r = await fetch(url, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const j = await r.json().catch(() => null);

      if (!r.ok || !j?.ok) {
        throw new Error(j?.message || "목록 조회 실패");
      }

      const list = Array.isArray(j.items) ? j.items : [];
      setRows(list);
    } catch (e) {
      console.error(e);
      alert("1:1 문의 목록을 불러오지 못했습니다.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    load();
  };

  return (
    <div className="space-y-4">
      {/* 헤더: 모바일 줄바꿈 대응 */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="text-xs text-slate-500">고객센터</div>
          <h2 className="text-lg font-semibold">1:1 문의 관리</h2>
          <div className="text-xs text-slate-500">
            고객이 접수한 문의를 확인하고 답변을 등록합니다.
          </div>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
          <button
            onClick={() => nav("/admin")}
            className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm hover:bg-slate-50 sm:w-auto"
          >
            관리자 홈
          </button>
          <button
            onClick={load}
            className="h-10 w-full rounded-lg bg-slate-900 px-3 text-sm text-white hover:brightness-110 sm:w-auto"
          >
            새로고침
          </button>
        </div>
      </div>

      {/* 탭 + 검색 (모바일 최적화) */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        {/* 탭: 모바일 가로 스크롤 */}
        <div className="-mx-4 px-4 overflow-x-auto">
          <div className="flex w-max items-center gap-2">
            {[
              ["all", "전체"],
              ["open", "대기"],
              ["answered", "답변완료"],
              ["closed", "종료"],
            ].map(([key, label]) => {
              const active = tab === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setTab(key as any)}
                  className={[
                    "rounded-full px-3 py-1 text-xs font-medium",
                    active
                      ? "bg-slate-900 text-white"
                      : "bg-slate-100 text-slate-700 hover:bg-slate-200",
                  ].join(" ")}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {/* 검색: 모바일 2줄 자연스러운 배치 */}
        <form onSubmit={onSearch} className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="이름/이메일/내용 검색"
            className="h-11 w-full rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-slate-400 sm:w-80"
          />
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={loading}
              className="h-11 flex-1 rounded-lg bg-slate-900 px-4 text-sm text-white hover:brightness-110 disabled:opacity-60 sm:flex-none sm:w-auto"
            >
              검색
            </button>
            <div className="h-11 flex items-center justify-center rounded-lg border border-slate-200 px-3 text-sm text-slate-600">
              {loading ? "..." : `${filteredCount}건`}
            </div>
          </div>
        </form>
      </div>

      {/* 리스트 영역 */}
      <div className="rounded-2xl border border-slate-200 bg-white">
        {/* 모바일 카드 */}
        <div className="space-y-3 p-4 md:hidden">
          {loading && !rows.length && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
              불러오는 중…
            </div>
          )}

          {!loading && !rows.length && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
              표시할 문의가 없습니다.
            </div>
          )}

          {rows.map((r) => (
            <button
              key={r.id}
              onClick={() => nav(`/admin/inquiries/${r.id}`)}
              className="w-full rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm hover:bg-slate-50"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="text-xs text-slate-500">번호 {r.id}</div>
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px]">
                  {formatStatus(r.status)}
                </span>
              </div>

              <div className="mt-1 text-[11px] text-slate-500">
                등록일자 {formatDate(r.created_at)}
              </div>

              <div className="mt-3 space-y-1 text-xs">
                <div className="flex justify-between gap-4">
                  <span className="text-slate-500">이름</span>
                  <span className="text-right font-medium">{r.name}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-slate-500">이메일</span>
                  <span className="text-right break-all">{r.email}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-slate-500">유형</span>
                  <span className="text-right">{formatCategory(r.category)}</span>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* 데스크톱 테이블 */}
        <div className="hidden md:block">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="w-20 px-3 py-3 text-left">번호</th>
                <th className="w-28 px-3 py-3 text-left">상태</th>
                <th className="w-32 px-3 py-3 text-left">유형</th>
                <th className="w-40 px-3 py-3 text-left">이름</th>
                <th className="w-64 px-3 py-3 text-left">이메일</th>
                <th className="w-56 px-3 py-3 text-left">등록일자</th>
                <th className="w-24 px-3 py-3 text-center">보기</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100">
                  <td className="px-3 py-2">{r.id}</td>
                  <td className="px-3 py-2">
                    <span className="rounded-full bg-slate-100 px-2 py-1 text-xs">
                      {formatStatus(r.status)}
                    </span>
                  </td>
                  <td className="px-3 py-2">{formatCategory(r.category)}</td>
                  <td className="px-3 py-2">{r.name}</td>
                  <td className="px-3 py-2 break-all">{r.email}</td>
                  <td className="px-3 py-2">{formatDate(r.created_at)}</td>
                  <td className="px-3 py-2 text-center">
                    <button
                      onClick={() => nav(`/admin/inquiries/${r.id}`)}
                      className="inline-flex items-center justify-center rounded-lg border border-slate-300 px-3 py-1 text-xs hover:bg-slate-50"
                    >
                      보기
                    </button>
                  </td>
                </tr>
              ))}

              {!rows.length && !loading && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-3 py-10 text-center text-slate-500"
                  >
                    표시할 문의가 없습니다.
                  </td>
                </tr>
              )}

              {loading && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-3 py-10 text-center text-slate-500"
                  >
                    불러오는 중…
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
